import React from "react";
import {
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
} from "@mui/material";

import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import { apipost } from "../../service/api";
import { US_STATES } from "../../utils/intakeValidation";
import CustomFieldsEditor, {
  rowsToCustomFields,
  validateCustomFieldRows,
} from "./CustomFieldsEditor";
import LogoSelect from "./LogoSelect";

/* -------------------- Validation -------------------- */
const validationSchema = yup.object({
  slug: yup.string().required("Slug is required"),
  title: yup.string().required("Title is required"),
  endpoint: yup.string().required("Endpoint is required"),
  // optional, but you can enforce if you want:
  // candidSlug: yup.string().trim().required("Candid Slug is required"),
});

/* -------------------- Component -------------------- */
export default function BuilderIntakeCreateForm() {
  const parseTextarea = (text = "") =>
    String(text)
      .split("\n")
      .map((v) => v.trim())
      .filter(Boolean);

  const formik = useFormik({
    initialValues: {
      slug: "",
      title: "",
      subtitle: "",
      intro: "",
      brandColor: "#b89b5e",
      endpoint: "lead/add",

      logoSrc: "",

      emailToText: "",
      loanOfficersText: "",
      agentNamesText: "",
      communitiesText: "",
      forwardCommitmentsText: "",
      urgencyText: "",
      dateFieldsText: "",

      // ✅ CHANGE: single string (like edit)
      candidSlug: "",

      // Admin-only US state for this builder (not shown on borrower form)
      state: "",

      fieldLabelOverrides: {
        taxRate: "",
      },

      smsOptIn: {
        enabled: false,
        consentText: "",
      },

      customFieldRows: [],
      hiddenFields: [],

      isActive: true,
    },

    validationSchema,

    onSubmit: async (values, { resetForm }) => {
      try {
        const customFieldsError = validateCustomFieldRows(values.customFieldRows);
        if (customFieldsError) {
          toast.error(customFieldsError);
          return;
        }

        const payload = {
          slug: values.slug,
          title: values.title,
          subtitle: values.subtitle,
          intro: values.intro,
          brandColor: values.brandColor,
          endpoint: values.endpoint,
          logoSrc: values.logoSrc,
          isActive: values.isActive,

          emailTo: parseTextarea(values.emailToText),
          loanOfficers: parseTextarea(values.loanOfficersText),
          agentNames: parseTextarea(values.agentNamesText),
          communities: parseTextarea(values.communitiesText),
          forwardCommitments: parseTextarea(values.forwardCommitmentsText),
          urgency: parseTextarea(values.urgencyText),
          dateFields: parseTextarea(values.dateFieldsText),

          // ✅ CHANGE: single string, trimmed (like edit)
          candidSlug: String(values.candidSlug || "").trim(),

          state: values.state,

          fieldLabelOverrides: values.fieldLabelOverrides,
          smsOptIn: values.smsOptIn,
          customFields: rowsToCustomFields(values.customFieldRows),
          hiddenFields: values.hiddenFields,
        };

        const result = await apipost("intake", payload);

        if (result?.status === 201) {
          toast.success("Builder intake created!");
          resetForm();
        } else {
          toast.error(result?.data?.message || "Failed to create intake");
        }
      } catch (err) {
        console.error(err);
        toast.error("Unexpected error submitting form.");
      }
    },
  });

  /* -------------------- Render -------------------- */
  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 900, mx: "auto", mt: 8 }}>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Create Builder Intake Form
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Slug"
              name="slug"
              value={formik.values.slug}
              onChange={formik.handleChange}
              error={formik.touched.slug && Boolean(formik.errors.slug)}
              helperText={formik.touched.slug && formik.errors.slug}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Title"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
            />
          </Grid>

          {/* ✅ NEW (like edit): single candid slug field */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Candid Slug"
              name="candidSlug"
              value={formik.values.candidSlug}
              onChange={formik.handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="State"
              name="state"
              value={formik.values.state}
              onChange={formik.handleChange}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              helperText="Admin-only — not shown on the borrower-facing form."
            >
              <option value="">Select a state</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name} ({s.code})
                </option>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Subtitle"
              name="subtitle"
              value={formik.values.subtitle}
              onChange={formik.handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Intro Text"
              name="intro"
              multiline
              rows={3}
              value={formik.values.intro}
              onChange={formik.handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="color"
              label="Brand Color"
              name="brandColor"
              value={formik.values.brandColor}
              onChange={formik.handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Submission Endpoint"
              name="endpoint"
              value={formik.values.endpoint}
              onChange={formik.handleChange}
            />
          </Grid>

          {/* -------- Logo Selector -------- */}
          <Grid item xs={12}>
            <LogoSelect
              value={formik.values.logoSrc}
              onChange={(url) => formik.setFieldValue("logoSrc", url)}
            />
          </Grid>

          {/* -------- Bulk Text Areas -------- */}
          {[
            ["Email Recipients", "emailToText", 4],
            ["Loan Officers", "loanOfficersText", 6],
            ["Agent Names", "agentNamesText", 8],
            ["Communities", "communitiesText", 8],
            ["Forward Commitments", "forwardCommitmentsText", 4],
            ["Urgency Options", "urgencyText", 4],
            ["Date Fields", "dateFieldsText", 2],
          ].map(([label, name, rows]) => (
            <Grid item xs={12} key={name}>
              <TextField
                fullWidth
                multiline
                minRows={rows}
                label={`${label} (one per line)`}
                name={name}
                value={formik.values[name]}
                onChange={formik.handleChange}
              />
            </Grid>
          ))}

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Tax Rate Label Override"
              value={formik.values.fieldLabelOverrides.taxRate}
              onChange={(e) =>
                formik.setFieldValue("fieldLabelOverrides.taxRate", e.target.value)
              }
              helperText="Example: CDD ($)"
            />
          </Grid>

          <CustomFieldsEditor
            rows={formik.values.customFieldRows}
            onRowsChange={(rows) => formik.setFieldValue("customFieldRows", rows)}
            hiddenFields={formik.values.hiddenFields}
            onHiddenFieldsChange={(hf) => formik.setFieldValue("hiddenFields", hf)}
          />

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.smsOptIn.enabled}
                  onChange={(e) =>
                    formik.setFieldValue("smsOptIn.enabled", e.target.checked)
                  }
                />
              }
              label="Enable SMS Opt-In"
            />
          </Grid>

          {formik.values.smsOptIn.enabled && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="SMS Consent Text"
                value={formik.values.smsOptIn.consentText}
                onChange={(e) =>
                  formik.setFieldValue("smsOptIn.consentText", e.target.value)
                }
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.isActive}
                  onChange={(e) =>
                    formik.setFieldValue("isActive", e.target.checked)
                  }
                />
              }
              label="Active"
            />
          </Grid>

          <Grid item xs={12}>
            <Button type="submit" variant="contained" fullWidth>
              Create Intake Form
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
}
